/**
 * Pluggable JWKS provider factory (drift `81f3f606`, wave item #21).
 *
 * The verifier (`verifyS2SToken` / `configureGrpcAuth({ jwksResolver })`)
 * has always taken a `GetKeyFn` — but the only ergonomic ways to BUILD one
 * were `createSigV4S3JWKSet` (private-S3) and reaching directly into jose's
 * `createLocalJWKSet` / `createRemoteJWKSet`. That left every downstream
 * service writing its own JWKS plumbing, and meant the canonical
 * `/.well-known/jwks.json` URL was effectively hardcoded per consumer with
 * no first-class test seam — so auth-transport e2e tests degraded to
 * `test.skipIf(!hasS2SInfra())` everywhere.
 *
 * `createJwksProvider(...)` is the single canonical constructor. It returns
 * a `GetKeyFn` the verifier consumes unchanged. The discriminated-union
 * input picks the source:
 *
 *   - `{ staticJwks }`  — TEST mode. Verify against an in-memory JWKS via
 *     `jose.createLocalJWKSet`; NO network. This is the seam that lets a
 *     test mint a JWT with a local private key and verify it offline (the
 *     D203 § "Test pattern" addendum — real jose + real JWKS shape, NOT a
 *     fabricated bypass).
 *   - `{ remoteUrl }`   — PROD (canonical, default). `jose.createRemoteJWKSet`
 *     over the HTTPS `/.well-known/jwks.json` endpoint — the exact D203
 *     "Verify (canonical impl)" primitive. Prod default behaviour is
 *     UNCHANGED: a consumer that wires the remote URL gets byte-identical
 *     semantics to the pre-existing direct `createRemoteJWKSet` usage.
 *   - `{ sigV4S3 }`     — the existing private-S3-bucket resolver
 *     (`createSigV4S3JWKSet`), surfaced through the same factory so callers
 *     have ONE import for all three sources.
 *
 * Prod stays the prod default: nothing here changes how a service that
 * passes a remote-URL (or SigV4-S3) provider behaves. The factory only
 * ADDS the static-JWKS test path + a uniform construction surface.
 */
import {
  type JWK,
  type JWTPayload,
  type RemoteJWKSetOptions,
  createLocalJWKSet,
  createRemoteJWKSet,
} from "jose";
import {
  type GetKeyFn,
  type SigV4S3RemoteJWKSetOptions,
  createSigV4S3JWKSet,
} from "./sigv4-s3-jwks";

/** RFC 7517 JWK Set object — what a `/.well-known/jwks.json` body deserializes to. */
export type JwksObject = { keys: JWK[] };

/**
 * Static-JWKS provider input — TEST mode. The verifier resolves keys from
 * this in-memory set; no network call is ever made. `kid` matching is
 * delegated to jose's `createLocalJWKSet` (rotation-aware: the set may
 * carry multiple keys; jose picks by the token's `kid` header, per D203).
 */
export type StaticJwksProviderOptions = {
  staticJwks: JwksObject;
  remoteUrl?: never;
  sigV4S3?: never;
};

/**
 * Remote-URL provider input — PROD (canonical, default). Wraps
 * `jose.createRemoteJWKSet` over the HTTPS `/.well-known/jwks.json`
 * endpoint per D203. `remoteOptions` is passed straight through to jose
 * (cooldown / cache / timeout tuning); omit it for jose's defaults.
 */
export type RemoteJwksProviderOptions = {
  remoteUrl: string | URL;
  remoteOptions?: RemoteJWKSetOptions;
  staticJwks?: never;
  sigV4S3?: never;
};

/**
 * SigV4-S3 provider input — the existing private-S3-bucket resolver,
 * surfaced through the unified factory. Identical to calling
 * `createSigV4S3JWKSet(opts)` directly.
 */
export type SigV4S3JwksProviderOptions = {
  sigV4S3: SigV4S3RemoteJWKSetOptions;
  staticJwks?: never;
  remoteUrl?: never;
};

/**
 * `createJwksProvider` input — exactly one of `staticJwks` / `remoteUrl` /
 * `sigV4S3`. The discriminated union makes "pass two sources" a compile
 * error so the source is always unambiguous.
 */
export type JwksProviderOptions =
  | StaticJwksProviderOptions
  | RemoteJwksProviderOptions
  | SigV4S3JwksProviderOptions;

/**
 * Build a `GetKeyFn` from a static JWKS, a remote `/.well-known/jwks.json`
 * URL, or the SigV4-S3 source. Pass the result to
 * `configureGrpcAuth({ jwksResolver })` or `verifyS2SToken(..., resolver)`.
 *
 * @example Prod (canonical, default — D203):
 * ```ts
 * const jwks = createJwksProvider({
 *   remoteUrl: `${NODII_AUTH_BASE_URL}/.well-known/jwks.json`,
 * });
 * configureGrpcAuth({ jwksResolver: jwks, ... });
 * ```
 *
 * @example Test (in-memory, no network — sign locally, verify offline):
 * ```ts
 * const jwks = createJwksProvider({ staticJwks: { keys: [publicJwk] } });
 * await verifyS2SToken(token, { ... }, ); // resolver wired via configure
 * ```
 */
export function createJwksProvider(opts: JwksProviderOptions): GetKeyFn {
  if ("staticJwks" in opts && opts.staticJwks !== undefined) {
    if (!Array.isArray(opts.staticJwks.keys)) {
      throw new Error(
        "createJwksProvider: staticJwks must be a JWK Set object ({ keys: JWK[] })",
      );
    }
    if (opts.staticJwks.keys.length === 0) {
      throw new Error(
        "createJwksProvider: staticJwks.keys is empty — at least one public JWK is required",
      );
    }
    // jose's local resolver is a `(protectedHeader, token) => Promise<KeyLike>`
    // — the exact `GetKeyFn` shape. It does the `kid` selection internally.
    const local = createLocalJWKSet(opts.staticJwks);
    return (async (protectedHeader, token) =>
      local(
        protectedHeader as Parameters<typeof local>[0],
        token as Parameters<typeof local>[1],
      ) as Promise<CryptoKey>) satisfies GetKeyFn;
  }

  if ("remoteUrl" in opts && opts.remoteUrl !== undefined) {
    const url =
      opts.remoteUrl instanceof URL ? opts.remoteUrl : new URL(opts.remoteUrl);
    const remote = createRemoteJWKSet(url, opts.remoteOptions);
    return (async (protectedHeader, token) =>
      remote(
        protectedHeader as Parameters<typeof remote>[0],
        token as Parameters<typeof remote>[1],
      ) as Promise<CryptoKey>) satisfies GetKeyFn;
  }

  if ("sigV4S3" in opts && opts.sigV4S3 !== undefined) {
    return createSigV4S3JWKSet(opts.sigV4S3);
  }

  throw new Error(
    "createJwksProvider: exactly one of { staticJwks, remoteUrl, sigV4S3 } is required",
  );
}

export type { JWTPayload };
